import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { identityGateway } from '../services';
import { ApiError } from '../types';
import { AuthShellLayout } from '../components/AuthShellLayout';
import type { Slide } from '../components/HeroCarousel';
import { PasswordField } from '../../../shared/components/PasswordField';
import { Button } from '../../../shared/components/Button';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const MIN_LENGTH = 8;

/** Mirrors ChangePasswordPage.tsx's own `clientSideComplexityError` — kept as a separate local copy
 * rather than extracted into a shared util in this pass, matching this codebase's existing convention of
 * per-page client-side complexity hints (the backend's `PasswordComplexity.Meets` is the actual source of
 * truth either way; this is only an early, friendlier hint before that round trip). */
function clientSideComplexityError(password: string): string | null {
  if (password.length < MIN_LENGTH) return `Must be at least ${MIN_LENGTH} characters.`;
  if (!/[A-Z]/.test(password)) return 'Must include an uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Must include a lowercase letter.';
  if (!/[0-9]/.test(password)) return 'Must include a digit.';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Must include a special character.';
  return null;
}

type TokenValidity = 'checking' | 'valid' | 'invalid';

export interface ResetPasswordPageProps {
  /** EPN-36 Partner Sign In overrides — every default reproduces Admin's own current copy/behavior
   * exactly; only PartnerResetPasswordPage passes anything else. The actual
   * `POST /api/auth/password/reset` call, token validation, and complexity rule are identical either way.
   * DESIGN-VS-REALITY GAP (disclosed, not built): the approved Partner Figma shows a live, two-column
   * 6-item password-rule checklist that updates as you type. This page keeps Admin's existing
   * after-submit-only complexity check instead — adding live-checklist behavior would change the shared
   * component both channels render, which is a real UI enhancement beyond this ticket's routing/
   * eligibility scope, not a Partner-specific override. See docs/epn-36-partner-signin-gaps.md. */
  heroSlides?: Slide[] | undefined;
  signInHref?: string;
  forgotPasswordHref?: string;
  heading?: string;
}

/**
 * Reached via the reset-password link sent by ForgotPasswordPage's flow. Branches within this ONE page
 * (Checking → invalid/expired → form → success) rather than one route per state, mirroring
 * AcceptInvitationPage.tsx's own established pattern for a token-driven anonymous flow. Never
 * auto-signs the user in on success — matches `POST /api/auth/password/reset`'s contract exactly (no
 * cookies are set by that endpoint).
 */
export function ResetPasswordPage({
  heroSlides,
  signInHref = '/login',
  forgotPasswordHref = '/forgot-password',
  heading = 'Reset Your Password',
}: ResetPasswordPageProps) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [validity, setValidity] = useState<TokenValidity>(token ? 'checking' : 'invalid');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  useEffect(() => {
    if (!token) {
      return;
    }
    let cancelled = false;
    identityGateway
      .validateResetToken(token)
      .then((valid) => {
        if (!cancelled) setValidity(valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (!cancelled) setValidity('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (newPassword !== confirmPassword) {
      setFieldError('New password and confirmation do not match.');
      return;
    }
    const complexityError = clientSideComplexityError(newPassword);
    if (complexityError) {
      setFieldError(complexityError);
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    try {
      await identityGateway.resetPassword(token, newPassword);
      setSucceeded(true);
    } catch (err) {
      // The backend distinguishes an invalid/expired link from a complexity failure with different
      // `detail` text (see PasswordRecoveryEndpoints on the backend) — both are safe to show verbatim,
      // unlike login's own enumeration-safe 401.
      if (err instanceof ApiError && err.status === 400 && err.messageIsServerAuthored) {
        setSubmitError(err.message);
      } else {
        setSubmitError(friendlyErrorMessage(err, "We couldn't reset your password right now."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (validity === 'checking') {
    return (
      <AuthShellLayout heroSlides={heroSlides}>
        <div className="auth-shell__form-slot">
          <p className="muted">Checking your reset link…</p>
        </div>
      </AuthShellLayout>
    );
  }

  if (validity === 'invalid') {
    return (
      <AuthShellLayout heroSlides={heroSlides}>
        <div className="auth-shell__form-slot">
          <h1>Reset Link Invalid</h1>
          <InlineAlert variant="error" data-testid="reset-password-invalid-message">
            This link is invalid or has expired.
          </InlineAlert>
          <p className="auth-shell__secondary-action">
            <Link to={forgotPasswordHref} className="auth-shell__inline-link">
              Request a new reset link
            </Link>
          </p>
        </div>
      </AuthShellLayout>
    );
  }

  if (succeeded) {
    return (
      <AuthShellLayout heroSlides={heroSlides}>
        <div className="auth-shell__form-slot">
          <h1>Password Reset</h1>
          <InlineAlert variant="success" data-testid="reset-password-success-message">
            Your password has been reset. Sign in with your new password.
          </InlineAlert>
          <div className="auth-shell__submit-row">
            <Link to={signInHref}>
              <Button type="button" className="auth-submit-button" data-testid="reset-password-go-to-login">
                Go to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </AuthShellLayout>
    );
  }

  return (
    <AuthShellLayout heroSlides={heroSlides}>
      <form onSubmit={handleSubmit} noValidate>
        <div className="auth-shell__form-slot">
          <h1>{heading}</h1>
        </div>

        <PasswordField
          label="New Password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          data-testid="reset-password-new-password-input"
          autoFocus
        />

        <PasswordField
          label="Confirm New Password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          data-testid="reset-password-confirm-password-input"
        />

        {fieldError && (
          <InlineAlert variant="error" data-testid="reset-password-field-error">
            {fieldError}
          </InlineAlert>
        )}
        {submitError && (
          <InlineAlert variant="error" data-testid="reset-password-submit-error">
            {submitError}
          </InlineAlert>
        )}

        <div className="auth-shell__submit-row">
          <Button
            type="submit"
            loading={submitting}
            loadingLabel="Resetting…"
            className="auth-submit-button"
            data-testid="reset-password-submit-button"
          >
            Reset Password
          </Button>
        </div>
      </form>
    </AuthShellLayout>
  );
}
